import { CElement, IConfigComponent, CDevice, CDeviceType, IForm, TEntity } from '@ts/ui-models';
import { useMemo, useRef, useState } from 'react';
import { CEntity } from '@ts/ui-models';
import { GridColDef } from '@mui/x-data-grid-pro';
import { WrapperEntitySelector } from '@components/Inputs';
import { Icon } from '@components/Outputs';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { postAttachDevice, postDetachDevice } from '@services/ApiService';
import { useAppDispatch } from '@redux/store';
import {
  Button,
  Grid,
  Select,
  Typography,
  generateDateColumn,
  generateEntityColumn,
  generateGlobalColumns,
  IDataGridConfig,
  IAction,
} from '@ts/storybook';
import WrapperDataGrid from '@modules/dataGrid/WrapperDataGrid';

interface IAttachmentTabProps extends IForm {
  isItem?: boolean;
}

const AttachmentTab = (props: IAttachmentTabProps) => {
  const tElement = new CElement(props.values);
  const isViewMode = props.mode === 'view';
  const dispatch = useAppDispatch();
  const dataGridRef = useRef<any>(null);

  const [state, setState] = useState<{ deviceType: TEntity; device: CEntity<any> | null }>({
    deviceType: 'any',
    device: null,
  });

  //#region Handlers
  const onChangeFormAttachDevice = (field: string) => (value: any) => {
    if (field === 'deviceType') {
      setState({ ...state, [field]: value, device: null });
    } else {
      setState({ ...state, [field]: value });
    }
  };
  const onClickAttachDevice = async () => {
    const response = await postAttachDevice(dispatch, tElement.getID(), state.device?.getID());

    if (response) {
      setState({ ...state, device: null });
      dataGridRef.current?.refreshDataGrid();
    }
  };
  const onClickDetachDevice = async (deviceId: string) => {
    const response = await postDetachDevice(dispatch, tElement.getID(), deviceId);
    if (response) {
      setState({ ...state, device: null });
      dataGridRef.current?.refreshDataGrid();
    }
  };

  //#endregion

  //#region Render
  const renderFormAttachDevice = () => {
    return (
      <Grid container spacing={1}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" text="Link new device" />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <Select
            label="Type"
            disabled={isViewMode}
            value={state.deviceType}
            onChange={onChangeFormAttachDevice('deviceType')}
            options={[
              { label: 'Physical device', value: 'physicalDevice' },
              { label: 'Passive tag', value: 'passiveDevice' },
            ]}
          />
        </Grid>
        {state.deviceType !== 'any' && (
          <Grid size={{ xs: 4 }}>
            <WrapperEntitySelector
              label="Device"
              searchComponent={generateSearchEntitySelector(state.deviceType)}
              value={state.device}
              onChangeEntity={onChangeFormAttachDevice('device')}
            />
          </Grid>
        )}
        {state.device && (
          <Grid size={{ xs: 2 }}>
            <Button variant="outlined" label="Attach" fullWidth onClick={onClickAttachDevice} startIcon={<Icon icon="link" />} />
          </Grid>
        )}
      </Grid>
    );
  };

  const renderDataGridAttachedDevice = () => {
    const defaultConfigComponent: IConfigComponent = {
      componentId: 'attachDevice',
      aclId: 'tsElement',
      //entity: 'item',
      fetchURL: `/api/v2/devicesAttached/${tElement.getID()}`,
    };
    const config: IDataGridConfig = {
      columnVisibilityModel: { id: false },
    };
    const extraAction = (): IAction => {
      return {
        id: 'detach',
        label: 'Detach',
        icon: 'LinkOff',
        fDisabled: (props, rowSelected) => {
          return Boolean(rowSelected.aliveEnd);
        },

        onClick: handleClick => {
          const tElement = new CEntity(handleClick().childElement);
          onClickDetachDevice(tElement.getID());

          //navigate(`/event/${entity.getID()}/view`);
        },
      };
    };

    const getColumns = useMemo((): GridColDef<any>[] => {
      const mixProps = { ...props, configComponent: defaultConfigComponent };

      const globalCells = generateGlobalColumns({ ...mixProps });
      return [
        globalCells.id(),
        generateEntityColumn(
          { field: 'childElement.device', headerName: 'Name / ID' },
          { tEntity: 'childElement.device', mainClass: CDevice }
        ),
        generateEntityColumn(
          { field: 'childElement.device.deviceType', headerName: 'Type' },
          { tEntity: 'childElement.device.deviceType', mainClass: CDeviceType }
        ),
        generateDateColumn({ field: 'aliveStart', headerName: 'Attachment date' }, { field: 'aliveStart' }),
        generateDateColumn({ field: 'aliveEnd', headerName: 'Detachchment date' }, { field: 'aliveEnd' }),
      ];
    }, [props, defaultConfigComponent]);

    return (
      <WrapperDataGrid
        ref={dataGridRef}
        title="Attached Devices"
        configComponent={defaultConfigComponent}
        dataGridConfig={config}
        columns={getColumns}
        extraActions={[extraAction()]}
        dataGridMode="server"
        withoutDefaultActions
      />
    );
  };

  //#endregion

  return (
    <Grid id="attachment-tab" container direction={'column'} spacing={4} size={12}>
      {!isViewMode && <Grid size={'auto'}>{renderFormAttachDevice()}</Grid>}
      <Grid size={'grow'}>{renderDataGridAttachedDevice()}</Grid>
    </Grid>
  );
};
export default AttachmentTab;
